/**
 * Virtual Staging Service - API calls for Virtual Staging feature
 * Handles property photo staging and video reel generation
 */

import { getStoredTokens, BASE_URL } from '../api/client';

// Room types for staging
// NOTE FOR BACKEND: New room types added - please update the API to accept these values
export const ROOM_TYPES = [
    // Primary rooms (most common)
    { id: 'living_room', name: 'Living Room', icon: 'Sofa', category: 'primary' },
    { id: 'bedroom', name: 'Bedroom', icon: 'Bed', category: 'primary' },
    { id: 'kitchen', name: 'Kitchen', icon: 'UtensilsCrossed', category: 'primary' },
    { id: 'dining_room', name: 'Dining Room', icon: 'Armchair', category: 'primary' },
    { id: 'bathroom', name: 'Bathroom', icon: 'Bath', category: 'primary' },
    { id: 'office', name: 'Home Office', icon: 'Monitor', category: 'primary' },
    { id: 'outdoor', name: 'Outdoor/Patio', icon: 'TreePine', category: 'primary' },

    // Secondary rooms (additional spaces)
    { id: 'kids_room', name: 'Kids Room', icon: 'ToyBrick', category: 'secondary' },
    { id: 'nursery', name: 'Nursery', icon: 'Baby', category: 'secondary' },
    { id: 'open_living_dining', name: 'Open Living Dining', icon: 'LayoutPanelTop', category: 'secondary' },
    { id: 'home_gym', name: 'Home Gym', icon: 'Dumbbell', category: 'secondary' },
    { id: 'game_room', name: 'Game Room', icon: 'Gamepad2', category: 'secondary' },
    { id: 'laundry_room', name: 'Laundry Room', icon: 'WashingMachine', category: 'secondary' },
    { id: 'studio', name: 'Studio', icon: 'Palette', category: 'secondary' },
    { id: 'sunroom', name: 'Sunroom', icon: 'Sun', category: 'secondary' },
    { id: 'hallway', name: 'Hallway', icon: 'DoorOpen', category: 'secondary' },

    // Specialty rooms
    { id: 'conference_room', name: 'Conference Room', icon: 'Presentation', category: 'specialty' },
    { id: 'spa_room', name: 'Spa Room', icon: 'Sparkles', category: 'specialty' },
    { id: 'wine_cellar', name: 'Wine Cellar', icon: 'Wine', category: 'specialty' },
    { id: 'home_theater', name: 'Home Theater', icon: 'Clapperboard', category: 'specialty' },
    { id: 'library', name: 'Library', icon: 'BookOpen', category: 'specialty' },
    { id: 'mudroom', name: 'Mudroom', icon: 'Footprints', category: 'specialty' },
    { id: 'garage', name: 'Garage', icon: 'Car', category: 'specialty' },
    { id: 'basement', name: 'Basement', icon: 'ArrowDownToLine', category: 'specialty' },
    { id: 'attic', name: 'Attic', icon: 'ArrowUpToLine', category: 'specialty' },

    // Other (custom)
    { id: 'other', name: 'Other', icon: 'Home', category: 'other' }
];

// Staging style presets
export const STAGING_STYLES = [
    { id: 'modern', name: 'Modern', description: 'Clean lines, minimalist furniture', color: 'from-slate-600 to-slate-800' },
    { id: 'scandinavian', name: 'Scandinavian', description: 'Light, airy, natural wood tones', color: 'from-amber-200 to-amber-400' },
    { id: 'contemporary', name: 'Contemporary', description: 'Polished, current finishes with broad buyer appeal', color: 'from-neutral-500 to-slate-700' },
    { id: 'industrial', name: 'Industrial', description: 'Exposed elements, urban style', color: 'from-zinc-500 to-zinc-700' },
    { id: 'transitional', name: 'Transitional', description: 'Classic meets contemporary', color: 'from-stone-400 to-stone-600' },
    { id: 'coastal', name: 'Coastal', description: 'Beach-inspired, relaxed feel', color: 'from-cyan-400 to-blue-500' },
    { id: 'farmhouse', name: 'Farmhouse', description: 'Rustic, cozy, welcoming', color: 'from-amber-600 to-orange-700' },
    { id: 'mid_century', name: 'Mid-Century Modern', description: 'Retro furniture, bold accents', color: 'from-orange-400 to-red-500' },
    { id: 'bohemian', name: 'Bohemian', description: 'Layered textures and relaxed editorial styling', color: 'from-fuchsia-400 to-rose-500' },
    { id: 'japandi', name: 'Japandi', description: 'Soft minimalism with warm wood and calm balance', color: 'from-stone-300 to-emerald-500' },
    { id: 'traditional', name: 'Traditional', description: 'Timeless furnishings with formal, familiar structure', color: 'from-amber-700 to-yellow-800' },
    { id: 'minimal_luxury', name: 'Minimal Luxury', description: 'Quiet upscale staging with refined materials', color: 'from-emerald-500 to-teal-700' },
    { id: 'luxury', name: 'Luxury', description: 'High-end, elegant finishes', color: 'from-yellow-400 to-amber-500' },
    { id: 'family_friendly', name: 'Family Friendly', description: 'Comfortable, practical staging for everyday living', color: 'from-lime-500 to-emerald-600' },
    { id: 'urban_professional', name: 'Urban Professional', description: 'Sophisticated city-ready styling with a clean edge', color: 'from-sky-500 to-indigo-600' },
    { id: 'luxury_listing', name: 'Luxury Listing', description: 'Statement staging tailored for premium properties', color: 'from-amber-300 to-orange-500' },
    { id: 'california_casual', name: 'California Casual', description: 'Sunlit indoor-outdoor styling with easy warmth', color: 'from-orange-300 to-yellow-500' },
    { id: 'parisian_classic', name: 'Parisian Classic', description: 'Elegant moldings, tailored furnishings, and soft sophistication', color: 'from-rose-300 to-stone-500' },
    { id: 'tokyo_minimal', name: 'Tokyo Minimal', description: 'Precise, quiet composition with restrained contemporary details', color: 'from-slate-400 to-slate-700' },
    { id: 'kyoto_zen', name: 'Kyoto Zen', description: 'Serene natural materials with a calm, meditative mood', color: 'from-emerald-300 to-stone-500' },
    { id: 'milan_modern', name: 'Milan Modern', description: 'Fashion-forward Italian styling with sculptural forms', color: 'from-neutral-400 to-zinc-700' },
    { id: 'copenhagen_light', name: 'Copenhagen Light', description: 'Soft Nordic brightness with warm minimal textures', color: 'from-sky-200 to-slate-400' },
    { id: 'london_townhouse', name: 'London Townhouse', description: 'Layered classic-modern balance with polished urban character', color: 'from-stone-500 to-slate-700' },
    { id: 'mediterranean_villa', name: 'Mediterranean Villa', description: 'Sun-washed warmth with organic textures and relaxed luxury', color: 'from-amber-300 to-orange-400' },
    { id: 'ibiza_organic', name: 'Ibiza Organic', description: 'Editorial resort style with limewash tones and natural forms', color: 'from-stone-200 to-amber-300' },
    { id: 'moroccan_modern', name: 'Moroccan Modern', description: 'Refined arches, earthy warmth, and artisan texture', color: 'from-orange-500 to-amber-700' }
];

// Job types for staging operations (legacy - kept for batch staging)
export const JOB_TYPES = [
    { id: 'full_staging', name: 'Full Staging', description: 'AI-powered virtual staging with your chosen style', credits: 75 }
];

// Session status values
export const SESSION_STATUS = {
    ACTIVE: 'active',
    COMPLETED: 'completed',
    EXPIRED: 'expired'
};

// Message roles
export const MESSAGE_ROLES = {
    USER: 'user',
    ASSISTANT: 'assistant',
    SYSTEM: 'system'
};

// Video aspect ratios
export const ASPECT_RATIOS = [
    { id: '9:16', name: 'Portrait (Reels)', width: 1080, height: 1920 },
    { id: '16:9', name: 'Landscape', width: 1920, height: 1080 },
    { id: '1:1', name: 'Square', width: 1080, height: 1080 }
];

// Video style presets for reels (Veo 3.1)
export const VIDEO_STYLES = [
    {
        id: 'luxury_modern',
        name: 'Luxury Modern',
        description: 'Atmospheric dolly-in with bokeh and golden hour light for a premium feel.',
        bestFor: 'High-end modern listings',
        icon: '🏢',
        isNew: true
    },
    {
        id: 'twilight_serenity',
        name: 'Twilight Serenity',
        description: 'Serene evening glide with warm interior amber glows and blue hour sky.',
        bestFor: 'Luxury evening vibes and exterior-to-interior flow',
        icon: '🌆',
        isNew: true
    },
    {
        id: 'architectural_materiality',
        name: '3D Materiality',
        description: 'Tactile focus on textures (wood, stone, fabric) with a powerful 3D parallax effect.',
        bestFor: 'Close-ups of high-end finishes and materials',
        icon: '🧱',
        isNew: true
    },
    {
        id: 'ethereal_life',
        name: 'Ethereal Life',
        description: 'Volumetric lighting (god rays) and subtle secondary motion like swaying curtains.',
        bestFor: 'Sun-drenched living areas and bedrooms',
        icon: '✨',
        isNew: true
    },
    {
        id: 'virtual_staging_fly_in',
        name: '3D Fly-In',
        description: 'Furniture pieces fly into the empty room from the sides and lock into position.',
        bestFor: 'Dramatic empty-to-furnished transformation',
        icon: '📦',
        isNew: true,
        is3D: true
    },
    {
        id: 'virtual_staging_assembly',
        name: '3D Assembly',
        description: 'Items materialize piece-by-piece in a logical sequence from an empty room.',
        bestFor: 'Detailed "building" effect',
        icon: '🏗️',
        isNew: true,
        is3D: true
    },
    {
        id: 'virtual_staging_drift',
        name: '3D Drift',
        description: 'All furniture and decor items drift in gracefully from the background.',
        bestFor: 'Smooth, magical transformation',
        icon: '🍃',
        isNew: true,
        is3D: true
    },
    {
        id: 'virtual_staging_drop',
        name: '3D Flow Drop',
        description: 'Furniture "flows" from the top to the floor in a gravity-drop sequence.',
        bestFor: 'Unique, high-impact assembly',
        icon: '💧',
        isNew: true,
        is3D: true
    },
    {
        id: 'sketch_to_reality',
        name: 'Sketch to Reality',
        description: 'Starts as an architectural sketch that magically morphs into a photorealistic staged room.',
        bestFor: 'The "Architect\'s Dream" reveal',
        icon: '✏️',
        isNew: true,
        is3D: true
    },
    {
        id: 'design_style_morph',
        name: 'Style Swapper',
        description: 'Fluidly morphs between different high-end aesthetics before settling on the final design.',
        bestFor: 'Showcasing design versatility',
        icon: '🔄',
        isNew: true,
        is3D: true
    },
    {
        id: 'ghost_staging_solidify',
        name: 'Potential Reveal',
        description: 'Furniture starts as shimmering "ghost" outlines and slowly solidifies into the room.',
        bestFor: 'Visualizing "potential" in empty spaces',
        icon: '👻',
        isNew: true,
        is3D: true
    },
    {
        id: 'spatial_reveal',
        name: 'Spatial Reveal',
        description: 'Dramatic 3D reveal with slow forward zoom and rack focus to sharp furniture.',
        bestFor: 'Opening shots and hero rooms',
        icon: '🔍',
        isNew: true,
        is3D: true
    },
    {
        id: 'hollywood_dramatic_reveal',
        name: 'Hollywood Reveal',
        description: 'Vertigo Effect (Dolly-Zoom) as the full furniture set materializes with god rays.',
        bestFor: 'The ultimate "Wow" factor for luxury listings',
        icon: '🌟',
        isNew: true,
        is3D: true
    },
    {
        id: 'cinematic',
        name: 'Cinematic',
        description: 'A classic, professional sweep of the room with a perfectly level horizon and soft lighting.',
        bestFor: 'General listings & open houses',
        icon: '🎬'
    },
    {
        id: 'smooth_pan',
        name: 'Smooth Pan',
        description: 'A steady, horizontal glide from left to right, capturing the full width of the space.',
        bestFor: 'Wide living areas and master suites',
        icon: '🎥'
    },
    {
        id: 'dynamic',
        name: 'Dynamic',
        description: 'An engaging mix of subtle zooms and vertical movement to highlight architectural depth.',
        bestFor: 'Social media reels & TikTok',
        icon: '⚡'
    },
    {
        id: 'elegant',
        name: 'Elegant',
        description: 'Graceful, artistic framing with a soft focus and warm, high-end marketing quality.',
        bestFor: 'Luxury properties & high-end staging',
        icon: '💎'
    },
    {
        id: 'the_reveal',
        name: 'The Reveal',
        description: 'Starts on a beautiful detail and slowly pulls back to unveil the full expanse of the room.',
        bestFor: 'Showcasing specific upgrades or views',
        icon: '✨'
    },
    {
        id: 'sunlight_dance',
        name: 'Sunlight Dance',
        description: 'Focuses on the play of natural light and shadows moving across the room\'s surfaces.',
        bestFor: 'Sunny rooms with large windows',
        icon: '☀️'
    },
    {
        id: 'aerial_sweep',
        name: 'Aerial Sweep',
        description: 'A smooth, high-angle descending shot that captures the flow and floor plan from above.',
        bestFor: 'Open concept layouts',
        icon: '🦅'
    },
    {
        id: 'steadicam_walk',
        name: 'Steadicam Walk',
        description: 'An immersive, first-person perspective that feels like walking through the front door.',
        bestFor: 'Virtual tours and walk-through reels',
        icon: '🚶'
    },
    {
        id: 'twilight_glow',
        name: 'Twilight Glow',
        description: 'Simulates the Blue Hour with cool ambient tones and warm, inviting interior lighting.',
        bestFor: 'Luxury evening vibes and cozy dens',
        icon: '🌆'
    }
];

// Music style presets for video reels
export const MUSIC_STYLES = [
    { id: 'luxury_lofi', name: 'Luxury Lo-Fi', description: 'Sophisticated ambient electronic beats for modern listings.' },
    { id: 'macro_detail', name: 'Macro Detail', description: 'Minimalist textural soundscape with rhythmic chimes.' },
    { id: 'cinematic_suspense', name: 'Cinematic Suspense', description: 'High-tension dramatic music for impact reveals.' },
    { id: 'ambient_piano', name: 'Ambient Piano', description: 'Soft, atmospheric piano' },
    { id: 'upbeat_modern', name: 'Upbeat Modern', description: 'Contemporary, energetic beats' },
    { id: 'acoustic', name: 'Acoustic', description: 'Warm guitar and strings' },
    { id: 'cinematic', name: 'Cinematic Orchestra', description: 'Dramatic orchestral score' },
    { id: 'chill_lofi', name: 'Chill Lo-Fi', description: 'Relaxed, modern vibes' },
    { id: 'none', name: 'No Music', description: 'Silent video' }
];

// Credit costs for different operations
export const CREDIT_COSTS = {
    full_staging: 75,
    video_reel: 750
};

// Resolution options for staging output
// Premium pricing for higher margins:
// 1K: 75 credits (base)
// 2K: 150 credits (+75)
// 4K: 300 credits (+225)
export const RESOLUTION_OPTIONS = [
    {
        value: '1k',
        label: '1K (1024×1024)',
        description: 'Good for social media',
        extraCredits: 0,
        badge: null,
        dimensions: { width: 1024, height: 1024 }
    },
    {
        value: '2k',
        label: '2K (2048×2048)',
        description: 'Great for websites & listings',
        extraCredits: 75,
        badge: 'Recommended',
        dimensions: { width: 2048, height: 2048 }
    },
    {
        value: '4k',
        label: '4K (4096×4096)',
        description: 'Best for print & marketing materials',
        extraCredits: 225,
        badge: 'Premium',
        dimensions: { width: 4096, height: 4096 }
    }
];

export const DEFAULT_RESOLUTION = '1k';

// Polling intervals and timeouts
export const IMAGE_POLL_INTERVAL = 3000;  // 3 seconds
export const IMAGE_POLL_TIMEOUT = 120000; // 2 minutes
export const VIDEO_POLL_INTERVAL = 5000;  // 5 seconds
export const VIDEO_POLL_TIMEOUT = 300000; // 5 minutes

/**
 * Check if user has access to Virtual Staging feature
 */
export function hasVirtualStagingAccess(subscription) {
    if (!subscription) return false;
    const tier = subscription?.tier?.toLowerCase() ||
                 subscription?.plan?.tier?.toLowerCase() ||
                 subscription?.plan?.name?.toLowerCase() ||
                 subscription?.effective_tier?.toLowerCase() ||
                 subscription?.effectiveTier?.toLowerCase() ||
                 'free';
    // Normalize rp-pro to rp_pro for consistent checking
    const normalizedTier = tier.replace(/-/g, '_');
    return ['ultra', 'enterprise', 'rp_starter', 'rp_pro', 'rp_ultra'].includes(normalizedTier);
}

/**
 * Fetch staging configuration from server
 */
export async function fetchStagingConfig() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/config`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        throw new Error(`Failed to fetch staging config: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch video configuration from server
 */
export async function fetchVideoConfig() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/video/config`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch video config: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch user's staging credits balance
 */
export async function fetchUserCredits() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/credits`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch credits: ${response.status}`);
    }

    return response.json();
}

/**
 * Upload a photo for staging
 */
export async function uploadPhoto(file, roomType) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('roomType', roomType);

    const response = await fetch(`${BASE_URL}/api/staging/photos`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: formData
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || errorData.detail;

        if (response.status === 422) {
            // Validation error - show specific message if available
            throw new Error(errorMessage || 'Validation error: Please check the image format and size');
        }
        if (response.status === 400) {
            throw new Error(errorMessage || 'Invalid file type or size. Please use JPEG, PNG, or WebP under 20MB.');
        }
        if (response.status === 403) {
            throw new Error(errorMessage || 'Subscription required for virtual staging');
        }
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 404) {
            throw new Error('Virtual staging service is not available. Please contact support.');
        }
        throw new Error(errorMessage || `Upload failed (${response.status}). Please try again.`);
    }

    return response.json();
}

/**
 * Fetch user's staged photos
 */
export async function fetchPhotos(limit = 20, cursor = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
        params.append('cursor', cursor);
    }

    const response = await fetch(`${BASE_URL}/api/staging/photos?${params}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch photos: ${response.status}`);
    }

    return response.json();
}

/**
 * Delete a photo
 */
export async function deletePhoto(photoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to delete photo: ${response.status}`);
    }

    return true;
}

/**
 * Start a staging job on a photo
 * @param {string} photoId - The photo ID to stage
 * @param {string} jobType - Type of job (empty_room, stage_room, full_staging)
 * @param {string} style - Staging style (modern, scandinavian, etc.)
 * @param {string} roomType - Type of room
 * @param {string} [customPrompt] - Optional custom instructions (e.g., "darker furniture", "add plants")
 * @param {string} [category] - Photo category: 'indoor' for virtual staging, 'outdoor' for sky replacement
 * @param {string} [outdoorMode] - Outdoor enhancement mode (twilight, sunset, etc.) - only used when category is 'outdoor'
 */
export async function startStagingJob(photoId, jobType, style, roomType, customPrompt = null, category = 'indoor', outdoorMode = 'twilight') {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const isOutdoor = category === 'outdoor';

    const body = {
        jobType,
        style: isOutdoor ? null : style, // Style only applies to indoor staging
        roomType,
        category,
        processingType: isOutdoor ? 'sky_replacement' : 'virtual_staging'
    };

    // Add outdoor mode for sky replacement
    if (isOutdoor && outdoorMode) {
        body.outdoorMode = outdoorMode;
    }

    // Add custom prompt if provided
    if (customPrompt && customPrompt.trim()) {
        body.customPrompt = customPrompt.trim();
    }

    const response = await fetch(`${BASE_URL}/api/staging/photos/${photoId}/stage`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        throw new Error(`Failed to start staging job: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch status of a staging job
 */
export async function fetchJobStatus(jobId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/jobs/${jobId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Job not found. The staging service may still be processing.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch job status: ${response.status}`);
    }

    return response.json();
}

/**
 * Start a video reel job
 */
export async function startVideoJob(photoId, aspectRatio, style, musicStyle, propertyName = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        aspectRatio,
        style,
        musicStyle
    };

    if (propertyName) {
        body.propertyName = propertyName;
    }

    const response = await fetch(`${BASE_URL}/api/staging/photos/${photoId}/video`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        throw new Error(`Failed to start video job: ${response.status}`);
    }

    return response.json();
}

/**
 * Start a video reel job from a session image
 */
export async function startVideoJobFromSession(sessionId, version, aspectRatio, style, musicStyle, propertyName = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        version,
        aspectRatio,
        style,
        musicStyle
    };

    if (propertyName) {
        body.propertyName = propertyName;
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions/${sessionId}/video`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        throw new Error(`Failed to start video job: ${response.status}`);
    }

    return response.json();
}

/**
 * Start a video reel job from an image URL
 * Used for batch/property photos that don't have a session
 */
export async function startVideoJobFromUrl(imageUrl, aspectRatio, style, musicStyle, propertyName = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        imageUrl,
        aspectRatio,
        style,
        musicStyle
    };

    if (propertyName) {
        body.propertyName = propertyName;
    }

    const response = await fetch(`${BASE_URL}/api/staging/video/from-url`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        throw new Error(`Failed to start video job: ${response.status}`);
    }

    return response.json();
}

/**
 * Submit batch video generation job
 * Processes multiple photos into videos asynchronously with email notification on completion
 * @param {Array} photos - Array of photo objects with photoId and imageUrl
 * @param {string} aspectRatio - Video aspect ratio (9:16, 16:9, 1:1)
 * @param {string} style - Video style (cinematic, dramatic, etc.)
 * @param {string} musicStyle - Music style (ambient_piano, upbeat, etc.)
 * @param {string} [propertyName] - Optional property name for email notification
 * @returns {Promise<{batchId: string, status: string, totalVideos: number, estimatedMinutes: number}>}
 */
export async function submitBatchVideoJob(photos, aspectRatio, style, musicStyle, propertyName = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        photos: photos.map(photo => ({
            photoId: photo.photoId || photo.photo_id || photo.id,
            imageUrl: photo.processedUrl || photo.processed_url || photo.stagedUrl || photo.staged_url || photo.url,
            roomType: photo.roomType || photo.room_type || 'room',
            s3Key: photo.s3Key || photo.processedS3Key || photo.processed_s3_key || null
        })),
        aspectRatio,
        style,
        musicStyle
    };

    if (propertyName) {
        body.propertyName = propertyName;
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos/batch`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const errorData = await response.json().catch(() => ({}));
            // API returns: { error, message, required, available }
            const error = new Error(errorData.message || errorData.detail?.message || 'Insufficient credits');
            error.code = errorData.error || 'INSUFFICIENT_CREDITS';
            error.required = errorData.required || errorData.detail?.required;
            error.available = errorData.available || errorData.detail?.available;
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `Failed to submit batch video job: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch batch video job status (optional polling)
 * @param {string} trackingUrl - The tracking URL returned from submitBatchVideoJob
 * @returns {Promise<{batchId, status, totalCount, completedCount, failedCount, videos}>}
 */
export async function fetchBatchVideoStatus(trackingUrl) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    // Normalize URL - handle both full URLs and relative paths
    const url = trackingUrl.startsWith('http') ? trackingUrl : `${BASE_URL}${trackingUrl}`;

    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Batch not found');
        }
        throw new Error('Failed to fetch batch video status');
    }

    return response.json();
}

/**
 * Fetch smart batch status (optional polling)
 * @param {string} trackingUrl - The tracking URL returned from submitSmartBatch
 * @returns {Promise<{batchId, status, progress, totalPhotos, completedCount, failedCount, photos}>}
 */
export async function fetchSmartBatchStatus(trackingUrl) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    // Normalize URL - handle both full URLs and relative paths
    const url = trackingUrl.startsWith('http') ? trackingUrl : `${BASE_URL}${trackingUrl}`;

    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Batch not found');
        }
        throw new Error('Failed to fetch batch status');
    }

    return response.json();
}

/**
 * Submit a pro video editing request
 * Sends video details and editing instructions to the pro editing team via email
 * @param {Object} request - The editing request details
 * @param {Array} request.videos - Array of raw video objects to be edited into one video
 * @param {Array} request.selectedPackages - Selected package IDs (e.g., ['simple', 'voiceover'])
 * @param {Array} request.packageNames - Display names of selected packages
 * @param {number} request.packagesTotal - Total cost of selected packages
 * @param {string} request.turnaround - Turnaround time ID (24h, 12h, 6h)
 * @param {string} request.turnaroundLabel - Display label for turnaround
 * @param {number} request.rushFee - Rush delivery fee
 * @param {number} request.totalPrice - Total price including rush fee
 * @param {string} request.instructions - Editing instructions from the user
 * @param {string} request.contactName - User's name
 * @param {string} request.contactEmail - User's email
 * @param {string} request.contactPhone - User's phone (optional)
 * @param {string} request.additionalNotes - Any additional notes
 * @param {Object} request.payment - Sanitized demo payment details
 * @returns {Promise<{success: boolean, message: string, requestId: string}>}
 */
export async function submitProEditingRequest(request) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        // Raw videos to be edited into one professional video
        videos: request.videos.map(video => ({
            videoId: video.id,
            name: video.name || video.title,
            videoUrl: video.videoUrl || video.video_url || video.resultUrl || video.result_url,
            shortUrl: video.shortUrl || video.short_url,
            thumbnailUrl: video.thumbnailUrl || video.thumbnail_url,
            duration: video.duration,
            createdAt: video.createdAt || video.created_at
        })),
        rawVideoCount: request.videos.length,
        // Selected editing packages
        selectedPackages: request.selectedPackages,
        packageNames: request.packageNames,
        packagesTotal: request.packagesTotal,
        // Delivery timing
        turnaround: request.turnaround,
        turnaroundLabel: request.turnaroundLabel,
        rushFee: request.rushFee,
        // Total
        totalPrice: request.totalPrice,
        // Request details
        instructions: request.instructions,
        contactName: request.contactName,
        contactEmail: request.contactEmail,
        contactPhone: request.contactPhone || null,
        additionalNotes: request.additionalNotes || null,
        payment: request.payment ? {
            demoMode: Boolean(request.payment.demoMode),
            paymentMethod: request.payment.paymentMethod || 'demo_card_form',
            cardBrand: request.payment.cardBrand || null,
            last4: request.payment.last4 || null,
            maskedCardNumber: request.payment.maskedCardNumber || null,
            expiryMonth: request.payment.expiryMonth || null,
            expiryYear: request.payment.expiryYear || null,
            cardholderName: request.payment.cardholderName || null,
            billingZip: request.payment.billingZip || null,
            collectionNote: request.payment.collectionNote || 'Demo-only payment collection'
        } : null,
        notifyEmail: 'support@cloudmortgage.io'
    };

    const response = await fetch(`${BASE_URL}/api/staging/videos/pro-editing-request`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || 'Failed to submit pro editing request');
    }

    return response.json();
}

/**
 * Fetch status of a video job
 */
export async function fetchVideoStatus(videoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos/${videoId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch video status: ${response.status}`);
    }

    return response.json();
}

/**
 * List all videos for the current user
 * @param {number} [limit=50] - Max videos to return
 * @returns {Promise<object>} - List of videos
 */
export async function listMyVideos(limit = 50) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos?limit=${limit}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch videos: ${response.status}`);
    }

    return response.json();
}

/**
 * Delete a video by ID
 * @param {string} videoId - The video ID to delete
 * @returns {Promise<void>}
 */
export async function deleteVideo(videoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos/${videoId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to delete video: ${response.status}`);
    }
}

/**
 * Refresh video URLs when they expire (403 error)
 * Call this when a video URL returns 403, then retry with the new URLs
 * @param {string} videoId - The video ID
 * @returns {Promise<{videoUrl: string, thumbnailUrl: string}>} - Fresh URLs
 */
export async function refreshVideoUrl(videoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos/${videoId}/refresh-url`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Video not found');
        }
        throw new Error(`Failed to refresh video URL: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Save a newly generated video to the user's library
 * @param {object} videoData - Video data to save
 * @param {string} videoData.videoUrl - URL to the video file
 * @param {string} [videoData.thumbnailUrl] - URL to video thumbnail
 * @param {string} [videoData.name] - Display name for the video
 * @param {string} [videoData.duration] - Video duration (e.g., "0:30")
 * @param {string} [videoData.sourceType] - Origin: "virtual_staging", "upload", etc.
 * @param {string} [videoData.sourceId] - ID of the source (e.g., session ID)
 * @param {object} [videoData.metadata] - Additional metadata
 * @returns {Promise<object>} - The saved video record
 */
export async function saveVideo(videoData) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    if (!videoData?.videoUrl) {
        throw new Error('videoUrl is required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(videoData)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to save video: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch available video variation presets
 * @returns {Promise<object>} - Presets grouped by category
 */
export async function fetchVariationPresets() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos/variations/presets`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch variation presets: ${response.status}`);
    }

    return response.json();
}

/**
 * Create video variations from a base video
 * @param {string} videoId - The base video ID
 * @param {string[]} variations - Array of variation preset keys to generate
 * @param {string} [namePrefix] - Optional prefix for variation names (e.g., property address)
 * @returns {Promise<object>} - Created variations with video URLs
 */
export async function createVideoVariations(videoId, variations, namePrefix = '') {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    if (!videoId) {
        throw new Error('videoId is required');
    }

    if (!variations?.length) {
        throw new Error('At least one variation preset is required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/videos/${videoId}/variations`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify({
            variations,
            namePrefix
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 400) {
            throw new Error(errorData.message || 'Invalid variation request - check preset names or base video');
        }
        if (response.status === 503) {
            throw new Error('Variation service temporarily unavailable. Please try again later.');
        }
        throw new Error(errorData.message || `Failed to create variations: ${response.status}`);
    }

    return response.json();
}

/**
 * Download multiple video variations as a zip file
 * @param {Array<{videoUrl: string, name: string}>} videos - Videos to include in zip
 * @param {string} zipName - Name for the zip file
 * @returns {Promise<void>}
 */
export async function downloadVariationsZip(videos, zipName = 'video-variations') {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();

    // Download each video and add to zip
    const downloadPromises = videos.map(async (video, index) => {
        try {
            const response = await fetch(video.videoUrl);
            if (!response.ok) throw new Error(`Failed to fetch ${video.name}`);
            const blob = await response.blob();
            const fileName = `${video.name || `variation-${index + 1}`}.mp4`;
            zip.file(fileName, blob);
        } catch (err) {
            console.error(`Failed to download ${video.name}:`, err);
            // Continue with other videos
        }
    });

    await Promise.all(downloadPromises);

    // Generate and download zip
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${zipName}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Upload a video file to S3 storage
 * @param {File} file - The video file to upload
 * @param {function} onProgress - Callback for progress updates (0-100)
 * @returns {Promise<object>} - The saved video record with videoUrl, shortUrl, etc.
 */
export async function uploadVideoFile(file, onProgress) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    if (!file) {
        throw new Error('No file provided');
    }

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('video', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Remove extension for display name
    formData.append('sourceType', 'upload');

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                const progress = Math.round((event.loaded / event.total) * 100);
                onProgress(progress);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                } catch (e) {
                    reject(new Error('Invalid response from server'));
                }
            } else {
                try {
                    const errorData = JSON.parse(xhr.responseText);
                    reject(new Error(errorData.message || `Upload failed: ${xhr.status}`));
                } catch (e) {
                    reject(new Error(`Upload failed: ${xhr.status}`));
                }
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', `${BASE_URL}/api/staging/videos/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${tokens.accessToken}`);
        xhr.send(formData);
    });
}

/**
 * Poll a job until complete with configurable intervals and timeout
 * @param {string} jobId - The job ID to poll
 * @param {'image' | 'video'} type - Type of job (affects polling intervals)
 * @param {function} onProgress - Callback for progress updates
 * @returns {Promise<object>} - The completed job result
 */
export async function pollJobUntilComplete(jobId, type = 'image', onProgress) {
    const interval = type === 'video' ? VIDEO_POLL_INTERVAL : IMAGE_POLL_INTERVAL;
    const timeout = type === 'video' ? VIDEO_POLL_TIMEOUT : IMAGE_POLL_TIMEOUT;
    const fetchFn = type === 'video' ? fetchVideoStatus : fetchJobStatus;

    const startTime = Date.now();
    let pollCount = 0;
    let displayedProgress = 0; // Track what we're showing to smooth transitions
    let continuousTimer = null;
    let currentMessage = 'Starting...';

    // Expected durations for smooth progress simulation
    const expectedDuration = type === 'video' ? 90000 : 25000; // 90s for video, 25s for image

    // Video generation stages for better UX
    const videoStages = [
        { threshold: 20, message: 'Preparing assets...' },
        { threshold: 45, message: 'Rendering frames...' },
        { threshold: 70, message: 'Encoding video...' },
        { threshold: 90, message: 'Final touches...' },
    ];

    // Image staging stages
    const imageStages = [
        { threshold: 25, message: 'Analyzing room...' },
        { threshold: 50, message: 'Designing layout...' },
        { threshold: 75, message: 'Rendering details...' },
        { threshold: 95, message: 'Enhancing quality...' },
    ];

    const stages = type === 'video' ? videoStages : imageStages;

    // Get message for current progress
    const getMessageForProgress = (progress) => {
        const stage = stages.find(s => progress < s.threshold) || stages[stages.length - 1];
        return stage.message;
    };

    // Start continuous progress simulation - always moving forward
    const startContinuousProgress = () => {
        if (continuousTimer) return;

        continuousTimer = setInterval(() => {
            const elapsed = Date.now() - startTime;
            // Calculate expected progress based on time, max 92% until completion confirmed
            const timeBasedProgress = Math.min(92, Math.floor((elapsed / expectedDuration) * 95));

            // Only increment if time-based is ahead and we're below 92%
            if (timeBasedProgress > displayedProgress && displayedProgress < 92) {
                displayedProgress = Math.min(displayedProgress + 1, timeBasedProgress, 92);
                currentMessage = getMessageForProgress(displayedProgress);
                if (onProgress) onProgress(displayedProgress, currentMessage);
            }
        }, 150); // Update every 150ms for smooth feel
    };

    const stopContinuousProgress = () => {
        if (continuousTimer) {
            clearInterval(continuousTimer);
            continuousTimer = null;
        }
    };

    // Smooth finish animation - increment to 100% over ~500ms
    const smoothFinish = (result, resolvePromise) => {
        stopContinuousProgress();

        const finishInterval = setInterval(() => {
            if (displayedProgress < 100) {
                displayedProgress = Math.min(displayedProgress + 2, 100);
                if (onProgress) onProgress(displayedProgress, displayedProgress >= 100 ? 'Complete!' : 'Finalizing...');
            } else {
                clearInterval(finishInterval);
                resolvePromise(result);
            }
        }, 50); // Fast finish animation
    };

    // Start continuous progress immediately
    startContinuousProgress();

    return new Promise((resolve, reject) => {
        const poll = async () => {
            try {
                pollCount++;
                const result = await fetchFn(jobId);

                // Handle different response formats for progress
                let serverProgress = result.progress ?? result.percent ?? result.percentage ?? null;

                // If server provides progress, use it as a minimum (don't go backwards)
                if (serverProgress !== null && serverProgress > 0) {
                    if (serverProgress > displayedProgress) {
                        displayedProgress = serverProgress;
                        currentMessage = getMessageForProgress(displayedProgress);
                        if (onProgress) onProgress(displayedProgress, currentMessage);
                    }
                }

                // Handle different status field names and values
                const status = (result.status || result.state || '').toUpperCase();

                if (status === 'COMPLETED' || status === 'COMPLETE' || status === 'SUCCESS' || status === 'DONE') {
                    // Smooth finish to 100%
                    smoothFinish(result, resolve);
                    return;
                }

                if (status === 'FAILED' || status === 'ERROR' || status === 'FAILURE') {
                    stopContinuousProgress();
                    const error = new Error(result.error || result.errorMessage || result.error_message || 'Job failed');
                    error.code = 'JOB_FAILED';
                    error.creditsRefunded = result.creditsRefunded || result.credits_refunded || false;
                    reject(error);
                    return;
                }

                // Check timeout
                if (Date.now() - startTime > timeout) {
                    stopContinuousProgress();
                    const error = new Error('Job timed out');
                    error.code = 'JOB_TIMEOUT';
                    reject(error);
                    return;
                }

                // Continue polling
                setTimeout(poll, interval);
            } catch (err) {
                stopContinuousProgress();
                console.error('[VirtualStaging] Poll error:', err);
                reject(err);
            }
        };

        poll();
    });
}

/**
 * Download an image from URL
 * Fetches as blob to ensure proper download behavior for cross-origin URLs
 */
export async function downloadImage(url, filename = 'staged-photo.jpg') {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error('Download failed:', err);
        // Fallback: open in new tab if fetch fails
        window.open(url, '_blank');
    }
}

/**
 * Download a video from URL
 * Fetches as blob to ensure proper download behavior for cross-origin URLs
 */
export async function downloadVideo(url, filename = 'property-reel.mp4') {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error('Download failed:', err);
        // Fallback: open in new tab if fetch fails
        window.open(url, '_blank');
    }
}

// ============================================================
// BATCH STAGING API FUNCTIONS
// ============================================================

/**
 * Submit a batch staging job
 * Processes multiple photos asynchronously - user receives email when complete
 *
 * @param {Array<{photoId: string, roomType: string}>} photos - Photos to stage with room assignments
 * @param {string} style - Staging style to apply to all photos
 * @param {string} jobType - Type of staging job (stage_room, full_staging, etc.)
 * @param {string} [propertyName] - Optional property name for organizing results
 * @returns {Promise<{batchId: string, estimatedMinutes: number}>}
 */
export async function submitBatchStagingJob(photos, style, jobType, propertyName = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        photos: photos.map(p => ({
            photoId: p.photoId || p.id,
            roomType: p.roomType
        })),
        style,
        jobType
    };

    if (propertyName) {
        body.propertyName = propertyName;
    }

    const response = await fetch(`${BASE_URL}/api/staging/batch`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to submit batch job: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch all batch staging jobs for the current user
 * @param {number} [limit=10] - Maximum jobs to return
 * @param {string} [cursor] - Pagination cursor
 */
export async function fetchBatchJobs(limit = 10, cursor = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
        params.append('cursor', cursor);
    }

    const response = await fetch(`${BASE_URL}/api/staging/batch?${params}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch batch jobs: ${response.status}`);
    }

    return response.json();
}

/**
 * Fetch status and details of a specific batch job
 * @param {string} batchId - The batch job ID
 */
export async function fetchBatchJobStatus(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/batch/${batchId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Batch job not found');
        }
        throw new Error(`Failed to fetch batch status: ${response.status}`);
    }

    return response.json();
}

/**
 * Download all staged photos from a batch as a ZIP file
 * @param {string} batchId - The batch job ID
 */
export async function downloadBatchResults(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/batch/${batchId}/download`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to download batch results: ${response.status}`);
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `staged-photos-${batchId}.zip`;
    if (contentDisposition) {
        // Try quoted filename first: filename="name.zip"
        let match = contentDisposition.match(/filename="([^"]+)"/);
        if (!match) {
            // Try unquoted filename: filename=name.zip
            match = contentDisposition.match(/filename=([^;\s]+)/);
        }
        if (match) {
            // Clean the filename - remove any trailing quotes or invalid chars
            filename = match[1].replace(/["']/g, '').trim();
            // Ensure it ends with .zip
            if (!filename.toLowerCase().endsWith('.zip')) {
                filename = filename.replace(/\.[^.]*$/, '') + '.zip';
            }
        }
    }

    // Download the blob
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Cancel a batch staging job (if still pending)
 * @param {string} batchId - The batch job ID
 */
export async function cancelBatchJob(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/batch/${batchId}/cancel`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to cancel batch job: ${response.status}`);
    }

    return response.json();
}

// ============================================================
// STAGING SESSIONS API (ChatGPT-like conversational refinement)
// ============================================================

/**
 * Create a new staging session with initial styling
 * This uploads the initial styling and generates the first staged image.
 * @param {string} photoId - ID of previously uploaded photo
 * @param {string} roomType - One of ROOM_TYPES
 * @param {string} style - One of STAGING_STYLES, or 'inspiration' for inspiration-based styling
 * @param {string} [customPrompt] - Optional initial instruction (max 500 chars)
 * @param {string} [resolution] - Output resolution: '1k', '2k', or '4k' (default: '1k')
 * @param {object} [inspirationData] - Optional inspiration data for inspiration-based styling
 * @param {boolean} [useStaged] - If true, use already-staged image as starting point (for batch photo refinement)
 */
export async function createStagingSession(photoId, roomType, style, customPrompt = null, resolution = DEFAULT_RESOLUTION, inspirationData = null, useStaged = false) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        photo_id: photoId,
        room_type: roomType,
        style: style,
        resolution: resolution
    };

    // For batch photos that are already staged, use the staged image as starting point
    if (useStaged) {
        body.useStaged = true;
    }

    if (customPrompt && customPrompt.trim()) {
        body.custom_prompt = customPrompt.trim();
    }

    // Include inspiration data for inspiration-based styling
    if (inspirationData) {
        body.inspiration = inspirationData;
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 400) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Photo not found or invalid request');
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        throw new Error(`Failed to create staging session: ${response.status}`);
    }

    return response.json();
}

/**
 * List all staging sessions for the current user
 * @param {number} [limit=20] - Max sessions to return (1-50)
 * @param {string} [cursor] - Pagination cursor
 */
export async function listStagingSessions(limit = 20, cursor = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
        params.append('cursor', cursor);
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions?${params}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to list sessions: ${response.status}`);
    }

    return response.json();
}

/**
 * Get session details including message history
 * @param {string} sessionId - The session ID
 * @param {boolean} [includeMessages=true] - Include full message history
 */
export async function getStagingSession(sessionId, includeMessages = true) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const params = new URLSearchParams({ include_messages: String(includeMessages) });

    const response = await fetch(`${BASE_URL}/api/staging/sessions/${sessionId}?${params}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Session not found');
        }
        throw new Error(`Failed to get session: ${response.status}`);
    }

    return response.json();
}

/**
 * Send a refinement instruction to refine the staged image
 * @param {string} sessionId - The session ID
 * @param {string} instruction - Natural language instruction (1-500 chars)
 * @param {number} [fromVersion] - Version to refine from (for branching). If null, uses latest version.
 */
export async function sendRefinement(sessionId, instruction, fromVersion = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        instruction: instruction.trim()
    };

    if (fromVersion !== null) {
        body.from_version = fromVersion;
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || `Failed to send refinement: ${response.status}`;

        if (response.status === 400) {
            const error = new Error(errorMessage);
            error.code = 'VALIDATION_ERROR';
            throw error;
        }
        if (response.status === 404) {
            throw new Error('Session not found');
        }
        throw new Error(errorMessage);
    }

    return response.json();
}

/**
 * Get version history for branching UI
 * @param {string} sessionId - The session ID
 */
export async function getVersionHistory(sessionId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions/${sessionId}/versions`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Session not found');
        }
        throw new Error(`Failed to get versions: ${response.status}`);
    }

    return response.json();
}

/**
 * Mark a session as completed (no more refinements allowed)
 * @param {string} sessionId - The session ID
 */
export async function completeStagingSession(sessionId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Session not found');
        }
        throw new Error(`Failed to complete session: ${response.status}`);
    }

    return response.json();
}

/**
 * Delete a session and all associated images
 * @param {string} sessionId - The session ID
 */
export async function deleteStagingSession(sessionId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Session not found');
        }
        throw new Error(`Failed to delete session: ${response.status}`);
    }

    return true;
}

/**
 * Upload multiple photos for batch staging
 * Returns array of photo objects with IDs for subsequent batch job submission
 * @param {FileList|Array<File>} files - Files to upload
 * @param {function} [onProgress] - Progress callback (current, total)
 */
export async function uploadPhotosForBatch(files, onProgress) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const results = [];
    const fileArray = Array.from(files);

    for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];

        const formData = new FormData();
        formData.append('file', file);
        formData.append('roomType', 'other'); // Default room type, will be assigned later

        const response = await fetch(`${BASE_URL}/api/staging/photos`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${tokens.accessToken}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to upload ${file.name}: ${response.status}`);
        }

        const result = await response.json();
        results.push({
            id: result.photoId || result.photo_id || result.id,
            originalUrl: result.url || result.originalUrl || result.original_url,
            thumbnailUrl: result.thumbnailUrl || result.thumbnail_url || result.url,
            name: file.name,
            roomType: 'other', // To be assigned by user
            file: file // Keep reference for preview
        });

        if (onProgress) {
            onProgress(i + 1, fileArray.length);
        }
    }

    return results;
}

// ============================================================
// SMART BATCH STAGING API (AI-powered room detection)
// ============================================================

/**
 * Outdoor enhancement modes for exterior photos
 */
export const OUTDOOR_MODES = [
    { value: 'twilight', label: 'Twilight', description: 'Deep blue sky with warm glowing windows', recommended: true },
    { value: 'sunset', label: 'Sunset', description: 'Golden orange sunset with dramatic clouds' },
    { value: 'sunrise', label: 'Sunrise', description: 'Soft pink/orange morning light' },
    { value: 'blue_hour', label: 'Blue Hour', description: 'Rich blue sky with amber window glow' },
    { value: 'dramatic', label: 'Dramatic', description: 'Dramatic clouds with enhanced lighting' },
    { value: 'golden_hour', label: 'Golden Hour', description: 'Warm golden directional sunlight' }
];

/**
 * Indoor room types for AI detection
 */
export const INDOOR_ROOM_TYPES = [
    'living_room', 'bedroom', 'master_bedroom', 'kitchen', 'bathroom',
    'dining_room', 'office', 'home_office', 'nursery', 'kids_room',
    'basement', 'attic', 'laundry', 'hallway', 'foyer',
    'closet', 'garage', 'gym', 'sunroom', 'bonus_room'
];

/**
 * Outdoor room types for AI detection
 */
export const OUTDOOR_ROOM_TYPES = [
    'exterior_front', 'exterior_back', 'exterior_side',
    'pool', 'patio', 'deck', 'backyard', 'garden',
    'driveway', 'balcony', 'rooftop', 'drone_aerial', 'porch'
];

/**
 * Get smart batch configuration (indoor styles, outdoor modes, etc.)
 * @returns {Promise<{indoorStyles: Array, outdoorModes: Array, creditsPerPhoto: number, maxPhotos: number}>}
 */
export async function getSmartBatchConfig() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/smart-batch/config`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
    }

    return response.json();
}

/**
 * Analyze photos for AI room type detection (no credits charged)
 * Uploads files one by one to provide accurate per-file progress
 * Falls back to batch upload if single-file endpoint not available
 * @param {FileList|Array<File>} files - Files to analyze
 * @param {function} [onProgress] - Progress callback (uploadedCount, totalCount, percent)
 * @returns {Promise<{photos: Array, batchId: string}>}
 */
export async function analyzePhotosForSmartBatch(files, onProgress) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const fileArray = Array.from(files);
    const totalFiles = fileArray.length;

    // Try single-file upload first for accurate progress
    try {
        const uploadedPhotos = [];
        let batchId = null;

        for (let i = 0; i < totalFiles; i++) {
            const file = fileArray[i];
            const formData = new FormData();
            formData.append('photo', file);
            if (batchId) {
                formData.append('batchId', batchId);
            }
            formData.append('photoIndex', i.toString());
            formData.append('totalPhotos', totalFiles.toString());

            const response = await fetch(`${BASE_URL}/api/staging/smart-batch/analyze-single`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${tokens.accessToken}`
                },
                body: formData
            });

            // If endpoint doesn't exist (404), fall back to batch upload
            if (response.status === 404) {
                throw new Error('FALLBACK_TO_BATCH');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to upload photo ${i + 1}`);
            }

            const result = await response.json();

            if (!batchId && result.batchId) {
                batchId = result.batchId;
            }

            if (result.photo) {
                uploadedPhotos.push(result.photo);
            }

            // Report real per-file progress
            if (onProgress) {
                const uploadedCount = i + 1;
                const percent = Math.round((uploadedCount / totalFiles) * 100);
                onProgress(uploadedCount, totalFiles, percent);
            }
        }

        if (uploadedPhotos.length === 0) {
            throw new Error('Failed to upload any photos. Please try again.');
        }

        return {
            photos: uploadedPhotos,
            batchId: batchId || `batch-${Date.now()}`
        };
    } catch (err) {
        // Fall back to batch upload if single-file endpoint not available
        if (err.message === 'FALLBACK_TO_BATCH' || err.message?.includes('404')) {
            return analyzePhotosForSmartBatchLegacy(files, onProgress);
        }
        throw err;
    }
}

/**
 * Legacy batch upload - uploads all files at once
 * Used as fallback when single-file endpoint not available
 */
async function analyzePhotosForSmartBatchLegacy(files, onProgress) {
    const tokens = getStoredTokens();
    const formData = new FormData();
    const fileArray = Array.from(files);
    const totalFiles = fileArray.length;

    fileArray.forEach((file, index) => {
        formData.append(`photo${index}`, file);
    });

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && onProgress) {
                const percent = Math.round((event.loaded / event.total) * 100);
                // Estimate file count from byte progress
                const estimatedCount = Math.min(Math.ceil((percent / 100) * totalFiles), totalFiles);
                onProgress(estimatedCount, totalFiles, percent);
            }
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch (e) {
                    reject(new Error('Invalid response from server'));
                }
            } else {
                try {
                    const errorData = JSON.parse(xhr.responseText);
                    reject(new Error(errorData.message || `Failed to analyze photos: ${xhr.status}`));
                } catch (e) {
                    reject(new Error(`Failed to analyze photos: ${xhr.status}`));
                }
            }
        };

        xhr.onerror = () => reject(new Error('Network error during upload'));

        xhr.open('POST', `${BASE_URL}/api/staging/smart-batch/analyze`);
        xhr.setRequestHeader('Authorization', `Bearer ${tokens.accessToken}`);
        xhr.send(formData);
    });
}

/**
 * Submit smart batch for processing (credits deducted upfront)
 * Photos should already be uploaded via analyzePhotosForSmartBatch - just pass photoIds with s3Keys
 * @param {Array} photos - Array of photo objects with photoId, s3Key, roomType, category, processingType
 * @param {string} indoorStyle - Style for indoor photos (null if using inspiration)
 * @param {string} outdoorMode - Enhancement mode for outdoor photos
 * @param {string} [propertyName] - Optional property name (deprecated, use propertyInfo.address)
 * @param {object} [inspiration] - Optional inspiration object with promptKeywords
 * @param {string} [batchId] - Batch ID from analyze response
 * @param {object} [propertyInfo] - Property information for gallery
 * @param {string} [propertyInfo.address] - Property address
 * @param {string} [propertyInfo.price] - Listing price (formatted)
 * @param {string} [propertyInfo.bedrooms] - Number of bedrooms
 * @param {string} [propertyInfo.bathrooms] - Number of bathrooms
 * @param {string} [propertyInfo.squareFeet] - Square footage (formatted)
 * @param {string} [propertyInfo.description] - Property description
 * @param {object} [branding] - Agent/brokerage branding settings
 * @param {object} [deliveryOptions] - Email/gallery delivery options
 * @param {string[]} [videoPhotoIds] - Array of photo IDs to generate videos for (max 3)
 * @returns {Promise<{batchId: string, status: string, totalPhotos: number, totalCredits: number, galleryUrl?: string}>}
 */
export async function submitSmartBatch(photos, indoorStyle, outdoorMode, propertyName = null, inspiration = null, batchId = null, propertyInfo = null, branding = null, deliveryOptions = null, videoSettings = null) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const body = {
        photos,
        outdoorMode,
        processingMode: "batch"  // Use Gemini Batch API for 50% cost savings
    };

    // Include batchId if provided (from analyze response)
    if (batchId) {
        body.batchId = batchId;
    }

    // Use either style or inspiration for indoor photos
    if (inspiration) {
        body.inspiration = {
            id: inspiration.id,
            name: inspiration.name,
            promptKeywords: inspiration.promptKeywords
        };
    } else if (indoorStyle) {
        body.indoorStyle = indoorStyle;
    }

    // Legacy propertyName support
    if (propertyName) {
        body.propertyName = propertyName;
    }

    // New property info for gallery - only include non-empty values
    if (propertyInfo) {
        const property = {};

        const address = propertyInfo.address || propertyInfo.propertyAddress;
        if (address && address.trim()) property.address = address.trim();

        const price = propertyInfo.price || propertyInfo.listingPrice;
        if (price && price.trim()) property.price = price.trim();

        // Convert to numbers, only include if valid
        if (propertyInfo.bedrooms && propertyInfo.bedrooms !== '') {
            const beds = parseInt(propertyInfo.bedrooms, 10);
            if (!isNaN(beds)) property.bedrooms = beds;
        }

        if (propertyInfo.bathrooms && propertyInfo.bathrooms !== '') {
            const baths = parseFloat(propertyInfo.bathrooms);
            if (!isNaN(baths)) property.bathrooms = baths;
        }

        const sqft = propertyInfo.squareFeet;
        if (sqft && sqft.trim()) property.squareFeet = sqft.trim();

        const desc = propertyInfo.description || propertyInfo.propertyDescription;
        if (desc && desc.trim()) property.description = desc.trim();

        // Only add property object if it has at least one field
        if (Object.keys(property).length > 0) {
            body.property = property;
        }
    }

    // Branding settings for gallery
    if (branding) {
        body.branding = {
            agentName: branding.agentName,
            agentTitle: branding.agentTitle,
            agentPhone: branding.agentPhone,
            agentEmail: branding.agentEmail,
            agentPhoto: branding.agentPhoto,
            brokerageName: branding.brokerageName,
            brokerageLogo: branding.brokerageLogo,
            accentColor: branding.accentColor,
            template: branding.defaultTemplate || branding.template
        };
    }

    // Delivery options
    if (deliveryOptions) {
        body.deliveryOptions = {
            sendEmail: deliveryOptions.sendEmail !== false, // Default true
            publishGallery: deliveryOptions.publishGallery !== false, // Default true
            includeQrCode: deliveryOptions.includeQrCode !== false, // Default true
            recipientEmail: deliveryOptions.recipientEmail
        };
    }

    // Video generation - select up to 3 photos for video reels with individual settings
    // Each videoSettings item: { photoId, style, aspectRatio }
    if (videoSettings && Array.isArray(videoSettings) && videoSettings.length > 0) {
        body.videoSettings = videoSettings.slice(0, 3).map(v => ({
            photoId: v.photoId,
            style: v.style || 'cinematic',
            aspectRatio: v.aspectRatio || '9:16'
        }));
    }

    const response = await fetch(`${BASE_URL}/api/staging/smart-batch`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 402) {
            const errorData = await response.json().catch(() => ({}));
            // API returns: { error, message, required, available }
            const error = new Error(errorData.message || errorData.detail?.message || 'Insufficient credits');
            error.code = errorData.error || 'INSUFFICIENT_CREDITS';
            error.required = errorData.required || errorData.detail?.required;
            error.available = errorData.available || errorData.detail?.available;
            throw error;
        }
        if (response.status === 403) {
            const error = new Error('Subscription required');
            error.code = 'SUBSCRIPTION_REQUIRED';
            throw error;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `Failed to submit smart batch: ${response.status}`);
    }

    return response.json();
}

/**
 * Update property info for an existing gallery/batch
 * Call this after batch submission to ensure property info is persisted
 * @param {string} batchId - The batch ID
 * @param {Object} propertyInfo - Property info object
 * @returns {Promise<Object>} Updated property info
 */
export async function updatePropertyInfo(batchId, propertyInfo) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    if (!batchId) {
        throw new Error('Batch ID required');
    }

    // Build the payload matching backend expectations
    const payload = {};

    const price = propertyInfo.price || propertyInfo.listingPrice;
    if (price) payload.price = typeof price === 'string' ? price : `$${price.toLocaleString()}`;

    if (propertyInfo.bedrooms !== undefined && propertyInfo.bedrooms !== '') {
        payload.beds = parseInt(propertyInfo.bedrooms, 10);
    }

    if (propertyInfo.bathrooms !== undefined && propertyInfo.bathrooms !== '') {
        payload.baths = parseFloat(propertyInfo.bathrooms);
    }

    if (propertyInfo.squareFeet !== undefined && propertyInfo.squareFeet !== '') {
        const sqft = typeof propertyInfo.squareFeet === 'string'
            ? parseInt(propertyInfo.squareFeet.replace(/,/g, ''), 10)
            : propertyInfo.squareFeet;
        if (!isNaN(sqft)) payload.sqft = sqft;
    }

    const desc = propertyInfo.description || propertyInfo.propertyDescription;
    if (desc) payload.description = desc;

    console.log('[updatePropertyInfo] Updating property for batch:', batchId, payload);

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[updatePropertyInfo] Failed:', errorData);
        throw new Error(errorData.message || errorData.detail || `Failed to update property: ${response.status}`);
    }

    console.log('[updatePropertyInfo] Success');
    return response.json();
}

/**
 * Get status of a smart batch
 * @param {string} batchId - The batch ID
 * @returns {Promise<Object>} - Batch details with photos and status
 */
export async function getSmartBatchStatus(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/smart-batch/${batchId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Batch not found');
        }
        throw new Error(`Failed to fetch batch status: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Fetch smart batch configuration (pricing, limits, supported types)
 * @returns {Promise<{batchFee: number, creditsPerPhoto: number, maxPhotos: number, indoorStyles: Array, outdoorModes: Array, supportedRoomTypes: Array}>}
 */
export async function fetchSmartBatchConfig() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/smart-batch/config`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch smart batch config: ${response.status}`);
    }

    return response.json();
}

/**
 * Publish a gallery (costs 100 credits)
 * @param {string} galleryId - The gallery/batch ID to publish
 * @returns {Promise<{galleryUrl: string, qrCodeUrl?: string}>}
 * @throws {Error} 402 if insufficient credits
 */
export async function publishGallery(galleryId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/galleries/${galleryId}/publish`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 402) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(errorData.message || 'Insufficient credits to publish gallery');
            error.code = 'INSUFFICIENT_CREDITS';
            error.required = 100;
            error.available = errorData.available || errorData.detail?.available;
            throw error;
        }
        if (response.status === 404) {
            throw new Error('Gallery not found');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to publish gallery: ${response.status}`);
    }

    return response.json();
}

// ============================================================
// PROPERTY MANAGEMENT API (CRUD for organizing batches)
// ============================================================

/**
 * Create a new property batch (draft)
 * @param {Object} propertyData - Property details
 * @returns {Promise<Object>} - Created property with batchId
 */
export async function createProperty(propertyData) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/properties`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(propertyData)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create property: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * List user's property batches
 * @param {Object} params - Query parameters (status, limit, cursor)
 * @returns {Promise<{properties: Array, nextCursor: string, hasMore: boolean}>}
 */
export async function listProperties(params = {}) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.cursor) queryParams.append('cursor', params.cursor);

    const url = `${BASE_URL}/api/staging/properties${queryParams.toString() ? '?' + queryParams : ''}`;

    const response = await fetch(url, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch properties: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Get a specific property batch
 * @param {string} batchId - The property batch ID
 * @returns {Promise<Object>} - Property details with photos
 */
export async function getProperty(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Property not found');
        }
        throw new Error(`Failed to fetch property: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Update a property batch
 * @param {string} batchId - The property batch ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - Updated property
 */
export async function updateProperty(batchId, updates) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: JSON.stringify(updates)
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update property: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Delete a property batch
 * @param {string} batchId - The property batch ID
 */
export async function deleteProperty(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 400) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Cannot delete property that is processing');
        }
        throw new Error(`Failed to delete property: ${response.status}`);
    }

    return true;
}

/**
 * Add photos to a property batch (auto-analyzed for room detection)
 * @param {string} batchId - The property batch ID
 * @param {FileList|Array<File>} files - Files to add
 * @param {function} [onProgress] - Progress callback
 * @returns {Promise<Object>} - Updated property with new photos
 */
export async function addPhotosToProperty(batchId, files, onProgress) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const formData = new FormData();
    const fileArray = Array.from(files);

    fileArray.forEach((file, index) => {
        formData.append(`photo${index}`, file);
    });

    if (onProgress) onProgress(0, fileArray.length);

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}/photos`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        },
        body: formData
    });

    if (onProgress) onProgress(fileArray.length, fileArray.length);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to add photos: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Remove a photo from a property batch
 * @param {string} batchId - The property batch ID
 * @param {string} photoId - The photo ID to remove
 * @returns {Promise<Object>} - Updated property
 */
export async function removePhotoFromProperty(batchId, photoId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to remove photo: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Start processing a property batch (credits deducted)
 * @param {string} batchId - The property batch ID
 * @returns {Promise<Object>} - Processing confirmation
 */
export async function processProperty(batchId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/staging/properties/${batchId}/process`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        if (response.status === 400) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Batch not ready for processing');
        }
        if (response.status === 402) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error(errorData.detail?.message || 'Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            error.required = errorData.detail?.required;
            error.available = errorData.detail?.available;
            throw error;
        }
        throw new Error(`Failed to process property: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
}

/**
 * Poll smart batch status until complete
 * @param {string} batchId - The batch ID
 * @param {function} [onProgress] - Callback for status updates
 * @param {number} [pollInterval=3000] - Polling interval in ms
 * @param {number} [timeout=600000] - Maximum wait time in ms (10 minutes)
 * @returns {Promise<Object>} - Completed batch data
 */
export async function pollSmartBatchUntilComplete(batchId, onProgress, pollInterval = 3000, timeout = 600000) {
    const startTime = Date.now();

    const poll = async () => {
        if (Date.now() - startTime > timeout) {
            throw new Error('Batch processing timeout');
        }

        const status = await getSmartBatchStatus(batchId);

        if (onProgress) {
            onProgress(status);
        }

        if (status.status === 'completed' || status.status === 'partial') {
            return status;
        }

        if (status.status === 'failed') {
            throw new Error('Batch processing failed');
        }

        // Still processing, wait and poll again
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        return poll();
    };

    return poll();
}

/**
 * Download results from a smart batch or property as ZIP
 * @param {string} batchId - The batch ID
 * @param {string} [type='smart-batch'] - Type: 'smart-batch' or 'properties'
 */
export async function downloadSmartBatchResults(batchId, type = 'smart-batch') {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const endpoint = type === 'properties'
        ? `${BASE_URL}/api/staging/properties/${batchId}/download`
        : `${BASE_URL}/api/staging/smart-batch/${batchId}/download`;

    const response = await fetch(endpoint, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to download results: ${response.status}`);
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `staged-photos-${batchId}.zip`;
    if (contentDisposition) {
        // Try quoted filename first: filename="name.zip"
        let match = contentDisposition.match(/filename="([^"]+)"/);
        if (!match) {
            // Try unquoted filename: filename=name.zip
            match = contentDisposition.match(/filename=([^;\s]+)/);
        }
        if (match) {
            // Clean the filename - remove any trailing quotes or invalid chars
            filename = match[1].replace(/["']/g, '').trim();
            // Ensure it ends with .zip
            if (!filename.toLowerCase().endsWith('.zip')) {
                filename = filename.replace(/\.[^.]*$/, '') + '.zip';
            }
        }
    }

    // Download the blob
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Convert a File to base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 encoded string
 */
export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
            const base64 = reader.result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = error => reject(error);
    });
}
